// Adapter around window.Telegram.WebApp. Returns null/false outside Telegram so
// the rest of the app stays platform-agnostic.

import { config } from '@/config';

interface TgThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  button_color?: string;
  button_text_color?: string;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe?: { user?: { id: number; first_name?: string; username?: string } };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TgThemeParams;
  isExpanded: boolean;
  expand(): void;
  ready(): void;
  close(): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink?(url: string): void;
  HapticFeedback?: { impactOccurred(s: 'light' | 'medium' | 'heavy'): void };
  MainButton?: { setText(t: string): void; show(): void; hide(): void; onClick(cb: () => void): void };
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
}

declare global {
  interface Window { Telegram?: { WebApp?: TgWebApp } }
}

const getWebApp = (): TgWebApp | null => window.Telegram?.WebApp ?? null;

export const telegram = {
  isAvailable(): boolean {
    const wa = getWebApp();
    return !!wa && typeof wa.initData === 'string' && wa.initData.length > 0;
  },

  initData(): string | null {
    const wa = getWebApp();
    return wa?.initData && wa.initData.length > 0 ? wa.initData : null;
  },

  applyTheme(): void {
    const wa = getWebApp();
    if (!wa) return;
    document.body.dataset.tg = '1';
    const root = document.documentElement;
    if (wa.themeParams.bg_color) root.style.setProperty('--tg-bg', wa.themeParams.bg_color);
  },

  ready(): void {
    const wa = getWebApp();
    if (!wa) return;
    wa.ready();
    if (config.telegram.expandOnLoad) wa.expand();
    if (config.telegram.closingConfirmation) wa.enableClosingConfirmation();
    this.applyTheme();
  },

  haptic(kind: 'light' | 'medium' | 'heavy' = 'light'): void {
    getWebApp()?.HapticFeedback?.impactOccurred(kind);
  },

  backButton(handler: (() => void) | null): void {
    const bb = getWebApp()?.BackButton;
    if (!bb) return;
    if (handler) {
      bb.onClick(handler);
      bb.show();
    } else {
      bb.hide();
    }
  },
};
